import React, { useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from './adminLayout';
import { Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

const EmailProviderForm = () => {
  const [providers, setProviders] = useState([
    { id: Date.now(), provider: 'Gmail', email: '', apiKey: '', showPassword: false },
  ]);
  const [availableEmails, setAvailableEmails] = useState([]);
  const [selectedSender, setSelectedSender] = useState('');
  const [images, setImages] = useState(Array(4).fill({ id: null, url: '', file: null }));

  const token = localStorage.getItem('token');

  const fetchAvailableEmails = async () => {
    try {
      const emailRes = await axios.get(`${API_URL}/api/mail-config/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const emails = emailRes.data.map((entry) => entry.email);
      setAvailableEmails(emails);
    } catch (error) {
      console.error('Error fetching emails:', error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      await fetchAvailableEmails();
      try {
        const imageRes = await axios.get(`${API_URL}/api/images/getall`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const imgData = imageRes.data;
        const imageUrls = [
          imgData.image1Url,
          imgData.image2Url,
          imgData.image3Url,
          imgData.image4Url,
        ]
          .filter(Boolean)
          .map((url) => ({
            id: null,
            url,
            file: null
          }));

        const filled = [...imageUrls, ...Array(4 - imageUrls.length).fill({ id: null, url: '', file: null })].slice(0, 4);
        setImages(filled);
      } catch (error) {
        console.error('Error fetching images:', error);
        setImages(Array(4).fill({ id: null, url: '', file: null }));
      }
    };

    fetchData();
  }, [token]);

  const handleProviderChange = (id, field, value) => {
    setProviders((prev) =>
      prev.map((p) => (p.id === id ? { ...p, [field]: value } : p))
    );
  };

  const togglePasswordVisibility = (id) => {
    setProviders((prev) =>
      prev.map((p) =>
        p.id === id ? { ...p, showPassword: !p.showPassword } : p
      )
    );
  };

  const addProvider = () => {
    setProviders((prev) => [
      ...prev,
      { id: Date.now(), provider: 'Gmail', email: '', apiKey: '', showPassword: false },
    ]);
  };

  const removeProvider = (id) => {
    setProviders((prev) => prev.filter((p) => p.id !== id));
  };

  const saveProviders = async () => {
    try {
      let saved = false;
      for (const provider of providers) {
        if (!provider.email || !provider.apiKey) continue;

        const mailConfigDTO = {
          email: provider.email,
          apiKey: provider.apiKey
        };

        await axios.post(`${API_URL}/api/mail-config/save`, mailConfigDTO, {
          headers: { Authorization: `Bearer ${token}` }
        });

        saved = true;
      }

      if (saved) {
        alert('Providers saved successfully!');
        setProviders([
          { id: Date.now(), provider: 'Gmail', email: '', apiKey: '', showPassword: false }
        ]);
        await fetchAvailableEmails();
      } else {
        alert('Please enter at least one valid email and API key.');
      }
    } catch (error) {
      alert('Failed to save providers.');
      console.error(error);
    }
  };

  const saveSender = async () => {
    if (!selectedSender) return alert('Select an email');
    try {
      await axios.put(`${API_URL}/api/mail-config/update-status/${selectedSender}?status=ACTIVE`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Sender email saved!');
    } catch (error) {
      alert('Failed to set sender.');
      console.error(error);
    }
  };

  const handleImageChange = (index, file) => {
    const updated = [...images];
    updated[index] = { ...updated[index], file };
    setImages(updated);
  };

  const saveAllImages = async () => {
    const formData = new FormData();
    const requiredFields = ['image1', 'image2', 'image3', 'image4'];

    let allFilesAdded = true;
    images.forEach((img, i) => {
      if (!img.file) {
        allFilesAdded = false;
      } else {
        formData.append(requiredFields[i], img.file);
      }
    });

    if (!allFilesAdded) {
      alert('Please upload all 4 images.');
      return;
    }

    try {
      const res = await axios.post(`${API_URL}/api/images/saveimg`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      const { image1Url, image2Url, image3Url, image4Url } = res.data;
      const updated = [
        { id: null, url: `${API_URL}${image1Url}`, file: null },
        { id: null, url: `${API_URL}${image2Url}`, file: null },
        { id: null, url: `${API_URL}${image3Url}`, file: null },
        { id: null, url: `${API_URL}${image4Url}`, file: null },
      ];
      setImages(updated);
      alert('Images updated successfully!');
    } catch (err) {
      console.error('Failed to upload images', err);
      alert('Image upload failed!');
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto mt-10 p-6 bg-white rounded-lg shadow-md">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold">Email Provider</h2>
            <p className="text-sm text-gray-600">Connect your email provider to send Emails</p>
          </div>
          <button
            onClick={addProvider}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
          >
            + Add Provider
          </button>
        </div>

        <div className="space-y-8">
          {providers.map((p) => (
            <div key={p.id} className="border rounded-md p-4 relative">
              {providers.length > 1 && (
                <button
                  onClick={() => removeProvider(p.id)}
                  className="absolute top-2 right-2 text-red-500 font-bold text-lg"
                >
                  ×
                </button>
              )}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Provider</label>
                  <select
                    className="w-full border rounded-md px-3 py-2"
                    value={p.provider}
                    onChange={(e) => handleProviderChange(p.id, 'provider', e.target.value)}
                  >
                    <option value="Gmail">Gmail</option>
                    <option value="Outlook">Outlook</option>
                    <option value="Yahoo">Yahoo</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div></div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email Address</label>
                  <input
                    type="email"
                    className="w-full border rounded-md px-3 py-2"
                    value={p.email}
                    onChange={(e) => handleProviderChange(p.id, 'email', e.target.value)}
                  />
                </div>
                <div className="relative">
                  <label className="block text-sm font-medium mb-1">API Key / App Password</label>
                  <input
                    type={p.showPassword ? 'text' : 'password'}
                    className="w-full border rounded-md px-3 py-2 pr-10"
                    value={p.apiKey}
                    onChange={(e) => handleProviderChange(p.id, 'apiKey', e.target.value)}
                  />
                  <span
                    className="absolute top-9 right-3 cursor-pointer text-gray-500"
                    onClick={() => togglePasswordVisibility(p.id)}
                  >
                    {p.showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={saveProviders}
          className="mt-6 bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700"
        >
          Save Providers
        </button>

        <div className="mt-12 border-t pt-6">
          <h3 className="text-lg font-semibold mb-2">Select Sender Email</h3>
          {availableEmails.length === 0 ? (
            <p className="text-gray-500 text-sm">No emails available.</p>
          ) : (
            <div className="flex items-center gap-4">
              <select
                className="w-full border rounded-md px-3 py-2"
                value={selectedSender}
                onChange={(e) => setSelectedSender(e.target.value)}
              >
                <option value="" disabled>
                  -- Select sender email --
                </option>
                {availableEmails.map((email) => (
                  <option key={email} value={email}>
                    {email}
                  </option>
                ))}
              </select>
            </div>
          )}
          <button
            onClick={saveSender}
            className="mt-6 bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700"
          >
            Set as Sender
          </button>
        </div>

        <div className="mt-12 border-t pt-6">
          <h3 className="text-lg font-semibold mb-2">Upload Campaign Images</h3>
          <div className="grid grid-cols-2 gap-6">
            {images.map((img, index) => (
              <div key={index} className="flex flex-col space-y-2 relative">
                {img.url && (
                  <img
                    src={img.url}
                    alt={`Uploaded ${index}`}
                    className="w-full h-40 object-cover border rounded"
                  />
                )}
                <input
                  type="file"
                  onChange={(e) => handleImageChange(index, e.target.files[0])}
                />
              </div>
            ))}
          </div>

          <div className="flex justify-end mt-4">
            <button
              onClick={saveAllImages}
              className="mt-6 bg-blue-600 text-white px-5 py-2 rounded-md hover:bg-blue-700"
            >
              Save Images
            </button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default EmailProviderForm;
